import {
  type ITankLimits,
  type ITankPlan,
  type ITankPlanCapacity,
} from "./tankPlans.model";

/** oilrec category codes → Tank Plan orb values. */
export const ORB_CATEGORY_TO_TANK_ORB: Partial<Record<string, string>> = {
  ORS: "OR",
  SST: "SS",
};

export type TankValueCheck = "limits" | "capacity";

export type TankValueCheckResult = {
  level: "ok" | "warn" | "error";
  message?: string;
};

export type TankListFilter = {
  bwl?: boolean;
  mfsbA?: boolean;
  mfsbB?: boolean;
  orbtype?: string;
};

const parseOptionalNumber = (value: string | undefined): number | null => {
  if (value === undefined || String(value).trim() === "") {
    return null;
  }
  const trimmed = String(value).trim();
  const n = Number(trimmed);
  return Number.isFinite(n) ? n : null;
};

const isBlankOrNumeric = (value: string | undefined): boolean => {
  if (value === undefined || String(value).trim() === "") return true;
  return Number.isFinite(Number(String(value).trim()));
};

export type TankLimitField = keyof ITankLimits | "capacity";

export type TankLimitValidationError = {
  message: string;
  field: TankLimitField;
};

/** Order: LL, L, H, HH, then capacity; blank skipped. */
export const validateTankLimitOrder = (
  limits: ITankLimits | undefined,
  capacity: ITankPlanCapacity | undefined,
): TankLimitValidationError | null => {
  if (!limits) return null;

  const labels: Array<{ key: keyof ITankLimits; label: string }> = [
    { key: "ll", label: "LL" },
    { key: "l", label: "L" },
    { key: "h", label: "H" },
    { key: "hh", label: "HH" },
  ];

  for (const { key, label } of labels) {
    if (!isBlankOrNumeric(limits[key])) {
      return { message: `${label} must be a number`, field: key };
    }
  }

  const ll = parseOptionalNumber(limits.ll);
  const l = parseOptionalNumber(limits.l);
  const h = parseOptionalNumber(limits.h);
  const hh = parseOptionalNumber(limits.hh);
  const cap = parseOptionalNumber(capacity?.value);

  const ordered: Array<{
    label: string;
    field: TankLimitField;
    value: number;
  }> = [];
  if (ll !== null) ordered.push({ label: "LL", field: "ll", value: ll });
  if (l !== null) ordered.push({ label: "L", field: "l", value: l });
  if (h !== null) ordered.push({ label: "H", field: "h", value: h });
  if (hh !== null) ordered.push({ label: "HH", field: "hh", value: hh });
  if (cap !== null)
    ordered.push({ label: "capacity", field: "capacity", value: cap });

  for (let i = 1; i < ordered.length; i++) {
    if (ordered[i].value < ordered[i - 1].value) {
      return {
        message: `${ordered[i - 1].label} must not exceed ${ordered[i].label}`,
        field: ordered[i].field,
      };
    }
  }

  return null;
};

/** Blank/non-numeric → ok; HH/LL/capacity → error; H/L → warn. */
export const checkTankValue = (
  value: string | number | undefined | null,
  tank: ITankPlan | undefined | null,
  checks: TankValueCheck[] = ["limits", "capacity"],
): TankValueCheckResult => {
  if (value === undefined || value === null || String(value).trim() === "") {
    return { level: "ok" };
  }

  const trimmed = String(value).trim();
  const num = Number(trimmed);
  if (!Number.isFinite(num)) {
    return { level: "ok" };
  }

  if (!tank) {
    return { level: "ok" };
  }

  const unit = tank.capacity?.unit ? ` ${tank.capacity.unit}` : "";

  if (checks.includes("capacity")) {
    const cap = parseOptionalNumber(tank.capacity?.value);
    if (cap !== null && num > cap) {
      return {
        level: "error",
        message: `Exceeds tank capacity (${cap}${unit})`,
      };
    }
  }

  if (checks.includes("limits") && tank.limits) {
    const { hh, h, l, ll } = tank.limits;
    const hhNum = parseOptionalNumber(hh);
    const hNum = parseOptionalNumber(h);
    const lNum = parseOptionalNumber(l);
    const llNum = parseOptionalNumber(ll);

    if (hhNum !== null && num > hhNum) {
      return {
        level: "error",
        message: `Exceeds HH (${hhNum}${unit})`,
      };
    }
    if (llNum !== null && num < llNum) {
      return {
        level: "error",
        message: `Below LL (${llNum}${unit})`,
      };
    }
    if (hNum !== null && num > hNum) {
      return {
        level: "warn",
        message: `Above H (${hNum}${unit})`,
      };
    }
    if (lNum !== null && num < lNum) {
      return {
        level: "warn",
        message: `Below L (${lNum}${unit})`,
      };
    }
  }

  return { level: "ok" };
};

export const resolveOrbType = (orbtype: string): string =>
  ORB_CATEGORY_TO_TANK_ORB[orbtype] ?? orbtype;

export const formatTankLimits = (limits: ITankLimits | undefined): string => {
  if (!limits) return "—";
  const parts: string[] = [];
  if (limits.hh !== undefined && String(limits.hh).trim() !== "") {
    parts.push(`HH ${limits.hh}`);
  }
  if (limits.h !== undefined && String(limits.h).trim() !== "") {
    parts.push(`H ${limits.h}`);
  }
  if (limits.l !== undefined && String(limits.l).trim() !== "") {
    parts.push(`L ${limits.l}`);
  }
  if (limits.ll !== undefined && String(limits.ll).trim() !== "") {
    parts.push(`LL ${limits.ll}`);
  }
  return parts.length > 0 ? parts.join(" / ") : "—";
};

export const filterTanks = (
  list: ITankPlan[] | undefined | null,
  filter: TankListFilter,
): ITankPlan[] => {
  if (!list?.length) return [];

  return list.filter(tank => {
    if (filter.bwl === true && !tank.bwl) return false;
    if (filter.mfsbA === true && !tank.mfsbA) return false;
    if (filter.mfsbB === true && !tank.mfsbB) return false;

    if (filter.orbtype) {
      const mapped = resolveOrbType(filter.orbtype);
      const orb = tank.orb ?? [];
      if (
        !orb.includes("*") &&
        !orb.includes(mapped) &&
        !orb.includes(filter.orbtype)
      ) {
        return false;
      }
    }

    return true;
  });
};
